import React, { useState, useEffect } from "react";
import {
  TextField,
  FormError,
  Select,
  Typography,
  Radio,
  Loader,
  Checkbox,
  TandCModal,
} from "components";
import { idOptions } from "utils/constants";
import { ROUTES } from "routes/routePaths";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import makeRequest from "services/makeRequest";
import { routes } from "routes/apiRoutes";
import {
  getAgentId,
  encryptData,
  isSuccessResponse,
  notFoundStatus,
  setAlert,
  getRegionOptions,
  isObjectEmpty,
} from "utils/helper";
import ButtonPair from "./ButtonPair";

import { Formik, Form, ErrorMessage } from "formik";
import { agentPersonalDetailsValidation } from "utils/FormValidations";

const PersonalDetails = ({ handleNext }) => {
  const navigate = useNavigate();

  const [showTIN, setShowTIN] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPEPDetails, setShowPEPDetails] = useState(false);
  const [aggregatorValidity, setAggregatorValidity] = useState({
    loading: false,
    details: null, //name and validationStatus {}
  });

  const [openTandCModal, setOpenTandCModal] = useState(false);

  const [existingPersonalDetails, setExistingPersonalDetails] = useState({});

  const [states, setStates] = useState([]);

  const [localGovtOptions, setLocalGovtOptions] = useState([]);

  const [agentState, setAgentState] = useState("");

  const [referralType, setReferralType] = useState({
    type: "",
    code: "",
  });

  const {
    aggregator,
    address,
    localGovt,
    region,
    state,
    nearestBusStop,
    identificationType,
    identificationNumber,
    corporate,
    tin,
    acceptedTC,
    corporateName,
    isPEP,
    pepDetails,
    referredBy,
    referralAgentCode,
  } = existingPersonalDetails;

  const {
    user,
    regions: { regions },
  } = useSelector((state) => state);

  //existing agent details would return agentId from token else, we check if there's any in the localstorage
  const agentId = user?.user?.agent_id || getAgentId();

  const parentId = user?.user?.parent_id;

  const initialValues = {
    aggregator: parentId ? false : aggregator,
    address: address || "",
    localGovt: localGovt || "",
    region: region || "",
    state: state || "",
    nearestBusStop: nearestBusStop || "",
    identificationType: identificationType || "",
    identificationNumber: identificationNumber || "",
    corporate,
    tin: tin || "",
    acceptedTC: acceptedTC || false,
    corporateName: corporateName || "",

    isPEP,
    pepDetails: pepDetails || "",
    referredBy: referredBy || "",
    referralAgentCode: referralAgentCode || "",
  };

  const getSelectedRegionStates = async (regionName) => {
    try {
      const selectedRegion = regions.find(
        (region) => region.name === regionName
      );

      if (selectedRegion) {
        const { data } = await makeRequest(
          "get",
          routes.fetchRegions(`region=${selectedRegion?.id}&type=1`)
        );

        if (isSuccessResponse(data)) {
          // console.log("HERE", data);
          setStates(data?.data);
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getSelectedStateLg = async (stateName) => {
    try {
      const selectedState = states.find((state) => state.name === stateName);
      if (selectedState) {
        const { data } = await makeRequest(
          "get",
          routes.fetchRegions(`state=${selectedState?.id}&type=2`)
        );
        if (isSuccessResponse(data)) {
          setLocalGovtOptions(getRegionOptions(data?.data));
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const onSubmit = async (values) => {
    if (parentId) {
      values.parentId = parentId;
    }

    try {
      let method = agentId ? "put" : "post";
      let url = agentId
        ? routes.updateAgentPersonalDetails(agentId)
        : routes.createAgentPersonalDetails();

      const makeSubmitRequest = async () => {
        const { data } = await makeRequest(method, url, values);

        if (isSuccessResponse(data)) {
          localStorage.setItem("agentId", encryptData(data.data.id));
          handleNext();
        } else {
          setAlert(data.responseMessage);
        }
      };

      if (referralType.type === "AGGREGATOR" && !aggregatorValidity.details) {
        if (await validateAggregatorCode()) {
          makeSubmitRequest();
        }
      } else {
        makeSubmitRequest();
      }
    } catch (err) {}
  };

  const validateAggregatorCode = async () => {
    try {
      setAggregatorValidity({
        loading: true,
        details: null,
        message: "", //in case its been said valid or otherwise, clear this status and recheck
      });

      const { data } = await makeRequest(
        "post",
        routes.aggregatorValidation(),
        { aggregatorCode: referralType.code }
      );

      if (isSuccessResponse(data)) {
        const info = data?.data?.data;

        aggregatorValidity.details = info;
        aggregatorValidity.loading = false;

        if (info) return true;
      } else {
        aggregatorValidity.msg = data.responseMessage;
        aggregatorValidity.loading = false;
        return false;
      }
    } catch (err) {
      return false;
    } finally {
      setAggregatorValidity({
        loading: false,
        ...aggregatorValidity,
      });
    }
  };

  useEffect(() => {
    const getAgentPersonalDetails = async () => {
      isObjectEmpty(existingPersonalDetails) && setLoading(true);
      try {
        const { data } = await makeRequest(
          "get",
          routes.getAgentPersonalDetails(`agentId=${agentId}`)
        );

        if (isSuccessResponse(data)) {
          const info = data?.data?.content[0].agentDetails;

          const { corporate, isPEP, referralAgentCode, referredBy } = info;
          setShowTIN(corporate);
          setShowPEPDetails(isPEP);
          setReferralType({
            ...referralType,
            code: referralAgentCode,
            type: referredBy,
          });

          if (referralAgentCode) validateAggregatorCode();
          //make sure when a user reloads and APi confirms files have been submitted, show the pending state
          if (info?.onBoardingState === 7)
            return navigate(ROUTES.agentOnboardingPending);

          setExistingPersonalDetails({ ...info });

          if (info.region) {
            await getSelectedRegionStates(info.region);

            if (info.state) {
              setAgentState(info.state);
              // await getSelectedStateLg(info.state);
            }
          }

          setLoading(false);
        }
      } catch (error) {
        if (notFoundStatus(error)) {
          setLoading(false);
        }
      }
    };
    agentId && getAgentPersonalDetails();

    //eslint-disable-next-line
  }, [agentId]);

  useEffect(() => {
    getSelectedStateLg(agentState);
    //eslint-disable-next-line
  }, [agentState]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center onboarding-loader">
        <Loader />
      </div>
    );
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={agentPersonalDetailsValidation(
          showTIN,
          referralType.type === "AGGREGATOR"
        )}
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          touched,
          isSubmitting,
          setFieldValue,
          getFieldProps,
        }) => (
          <Form noValidate autoComplete="off">
            {!parentId && (
              <>
                {" "}
                <Typography state="grey" variant="body2">
                  How do you want to register?
                  <span style={{ marginLeft: ".2rem" }} className="font-danger">
                    *
                  </span>
                </Typography>
                <div>
                  <Radio
                    value={values.aggregator}
                    checked={values.aggregator === false}
                    onClick={() => {
                      setFieldValue("aggregator", false);
                    }}
                  />
                  <label htmlFor="isAgent" className="register-label">
                    As an agent
                  </label>

                  <div className="phone-option">
                    <Radio
                      value={values.aggregator}
                      checked={values.aggregator === true}
                      onClick={() => {
                        setFieldValue("aggregator", true);
                      }}
                    />
                    <label htmlFor="aggregator" className="register-label">
                      As an aggregator
                    </label>
                  </div>
                  <ErrorMessage component={FormError} name="aggregator" />
                </div>
              </>
            )}

            <Typography state="grey" variant="body2" className={"mt-3"}>
              Are you a corporate agent?
              <span style={{ marginLeft: ".2rem" }} className="font-danger">
                *
              </span>
            </Typography>
            <div>
              <Radio
                value={values.corporate}
                checked={values.corporate === true}
                onClick={() => {
                  setShowTIN(true);
                  setFieldValue("corporate", true);
                }}
              />
              <label htmlFor="corporate" className="register-label">
                Yes
              </label>

              <div className="phone-option">
                <Radio
                  value={values.corporate}
                  checked={values.corporate === false}
                  onClick={() => {
                    setShowTIN(false);
                    setFieldValue("tin", ""); //Be sure to not send anything if corporate is false
                    setFieldValue("corporateName", "");
                    setFieldValue("corporate", false);
                  }}
                />
                <label htmlFor="corporate" className="register-label">
                  No
                </label>
              </div>
              <ErrorMessage component={FormError} name="corporate" />

              <div className="row">
                {showTIN && (
                  <div className="">
                    <TextField
                      name="corporateName"
                      type="text"
                      placeholder="Corporate Name"
                      label="Corporate Name"
                      {...getFieldProps("corporateName")}
                      containerClassName={"mt-3 mb-4"}
                      hasError={errors.corporateName && touched.corporateName}
                    />
                    <ErrorMessage component={FormError} name="corporateName" />
                  </div>
                )}

                <>
                  {" "}
                  <Typography state="grey" variant="body2">
                    Are you or related to a politically exposed person (PEP)?
                    <span
                      style={{ marginLeft: ".2rem" }}
                      className="font-danger"
                    >
                      *
                    </span>
                  </Typography>
                  <div>
                    <Radio
                      value={values.isPEP}
                      checked={values.isPEP === true}
                      onClick={() => {
                        setFieldValue("isPEP", true);
                        setShowPEPDetails(true);
                      }}
                    />
                    <label
                      htmlFor="politically exposed"
                      className="register-label"
                    >
                      Yes
                    </label>

                    <div className="phone-option">
                      <Radio
                        value={values.isPEP}
                        checked={values.isPEP === false}
                        onClick={() => {
                          setFieldValue("isPEP", false);
                          setShowPEPDetails(false);
                          setFieldValue("pepDetails", ""); //Be sure to not send anything if corporate is false
                        }}
                      />
                      <label htmlFor="isPEP" className="register-label">
                        No
                      </label>
                    </div>
                    <ErrorMessage component={FormError} name="isPEP" />
                  </div>
                </>
                {showPEPDetails && (
                  <div className="">
                    <TextField
                      name="pepDetails"
                      type="text"
                      rows="2"
                      placeholder="Enter text"
                      required={false}
                      label="Kindly provide further details below"
                      {...getFieldProps("pepDetails")}
                      containerClassName={"mt-3"}
                    />
                  </div>
                )}

                <div className="col-md-6 mt-3">
                  <Select
                    label="Select Region"
                    placeholder="Select Region"
                    options={getRegionOptions(regions)}
                    loading={!regions.length}
                    name="region"
                    onChange={(e) => {
                      let val = e.target.value;
                      setStates([]);
                      setFieldValue("state", "");
                      setFieldValue("localGovt", "");
                      setFieldValue("region", val);
                      getSelectedRegionStates(val);
                    }}
                    value={values.region}
                    hasError={errors.region && touched.region}
                  />
                  <ErrorMessage component={FormError} name="region" />
                </div>
                <div className="col-md-6 mt-3">
                  <Select
                    label="Select State"
                    placeholder="Select state"
                    options={getRegionOptions(states)}
                    name="state"
                    onChange={(e) => {
                      let val = e.target.value;
                      setLocalGovtOptions([]);
                      setFieldValue("localGovt", "");
                      setFieldValue("state", val);
                      getSelectedStateLg(val);
                    }}
                    value={values.state}
                    disabled={!values.region}
                    // {...getFieldProps("state")}
                    hasError={errors.state && touched.state}
                  />
                  <ErrorMessage component={FormError} name="state" />
                </div>

                <div className="col-md-6 mt-3">
                  <Select
                    label="Select Local Government"
                    placeholder="Select Local government"
                    options={localGovtOptions}
                    disabled={!values.state}
                    name="localGovt"
                    {...getFieldProps("localGovt")}
                    hasError={errors.localGovt && touched.localGovt}
                  />
                  <ErrorMessage component={FormError} name="localGovt" />
                </div>

                <div className="col-md-6">
                  <TextField
                    name="address"
                    type="text"
                    placeholder="Enter Address"
                    label="Address"
                    {...getFieldProps("address")}
                    containerClassName={"mt-3"}
                    hasError={errors.address && touched.address}
                  />
                  <ErrorMessage component={FormError} name="address" />
                </div>

                <div className="col-md-6">
                  <TextField
                    name="nearestBusStop"
                    type="text"
                    placeholder="Enter Nearest Bus stop"
                    label="Nearest Bus stop"
                    {...getFieldProps("nearestBusStop")}
                    containerClassName={"mt-3"}
                    hasError={errors.nearestBusStop && touched.nearestBusStop}
                  />
                  <ErrorMessage component={FormError} name="nearestBusStop" />
                </div>

                <div className="col-md-6 mt-3">
                  <Select
                    label="Identification Type"
                    placeholder="Select Identification Type"
                    options={idOptions}
                    name="identificationType"
                    {...getFieldProps("identificationType")}
                    hasError={
                      errors.identificationType && touched.identificationType
                    }
                  />
                  <ErrorMessage
                    component={FormError}
                    name="identificationType"
                  />
                </div>

                <div className="col-md-6">
                  <TextField
                    name="identificationNumber"
                    type="text"
                    placeholder="Enter Identification Number"
                    label="Identification Number"
                    {...getFieldProps("identificationNumber")}
                    containerClassName={"mt-3"}
                    hasError={
                      errors.identificationNumber &&
                      touched.identificationNumber
                    }
                  />
                  <ErrorMessage
                    component={FormError}
                    name="identificationNumber"
                  />
                </div>

                {showTIN && (
                  <div className="col-md-6">
                    <TextField
                      name="tin"
                      type="text"
                      placeholder="Enter Tax Identification Number"
                      label="Tax Identification Number"
                      {...getFieldProps("tin")}
                      containerClassName={"mt-3"}
                      hasError={errors.tin && touched.tin}
                    />
                    <ErrorMessage component={FormError} name="tin" />
                  </div>
                )}
              </div>

              <div className="mt-3">
                <Select
                  label="How did you hear about PocketMoni TMS Portal"
                  placeholder="Select a method"
                  options={[
                    {
                      label: "Friends And Family",
                      value: "FRIENDS AND FAMILY",
                    },
                    { label: "Aggregator", value: "AGGREGATOR" },
                    { label: "Social Media", value: "SOCIAL MEDIA" },
                  ]}
                  name="referredBy"
                  value={values.referredBy}
                  onChange={(e) => {
                    setFieldValue("referredBy", e.target.value);
                    setFieldValue("referralAgentCode", "");
                    setReferralType({ ...referralType, type: e.target.value });
                  }}
                  hasError={errors.referredBy && touched.referredBy}
                />
                <ErrorMessage component={FormError} name="referredBy" />
              </div>

              {values.referredBy?.toUpperCase() === "AGGREGATOR" && (
                <div className="col-mt-3">
                  <TextField
                    name="referralAgentCode"
                    type="text"
                    placeholder="Aggregator Code"
                    label="Enter Aggregator Code"
                    value={values.referralAgentCode}
                    onChange={(e) => {
                      let val = e.target.value;
                      setFieldValue("referralAgentCode", val);
                      setReferralType({ ...referralType, code: val });
                      setAggregatorValidity({
                        loading: false,
                        details: null,
                        msg: "",
                      });
                    }}
                    // onBlur={validateAggregatorCode}
                    containerClassName={"mt-3"}
                    hasError={
                      errors.referralAgentCode && touched.referralAgentCode
                    }
                  />
                  <ErrorMessage
                    component={FormError}
                    name="referralAgentCode"
                  />
                  {values.referralAgentCode && !aggregatorValidity.loading && (
                    <Typography
                      className="text-underline cursor-pointer"
                      variant="caption1"
                      state="primary"
                      onClick={validateAggregatorCode}
                    >
                      Validate Aggregator Code
                    </Typography>
                  )}

                  {values.referralAgentCode && (
                    <div className="mt-1">
                      {/* Currently validating aggregator code */}
                      {aggregatorValidity.loading ? (
                        <>
                          <Typography variant="caption1" state="secondary">
                            Validating Aggregator Code{" "}
                            <span style={{ marginLeft: "1rem" }}>
                              <Loader
                                variant="secondary"
                                height={3}
                                width={1}
                                margin={-12}
                                color="#1966FC"
                              />
                            </span>
                          </Typography>
                        </>
                      ) : (
                        ""
                      )}

                      {/* Aggregator code is said valid */}
                      {aggregatorValidity.details?.validated ? (
                        <div className="d-flex align-items-center">
                          <Typography variant="caption1" state="success">
                            PM Aggregator found{" "}
                          </Typography>
                          &nbsp;
                          <Typography variant="caption2" state="">
                            - {aggregatorValidity.details?.name}{" "}
                          </Typography>
                        </div>
                      ) : (
                        ""
                      )}

                      {/* Aggregator code is not valid */}
                      {aggregatorValidity.msg ? (
                        <Typography variant="caption1" state="danger">
                          {aggregatorValidity.msg}
                        </Typography>
                      ) : (
                        ""
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="d-flex align-items-center mt-3">
                <Checkbox
                  name="acceptedTC"
                  checked={values.acceptedTC}
                  onChange={(e) =>
                    setFieldValue("acceptedTC", e.target.checked)
                  }
                />{" "}
                &nbsp;&nbsp;
                <Typography component="span" variant="body4">
                  I have read and I accept the{" "}
                  <Typography
                    variant="body4"
                    state="secondary"
                    component="span"
                    className={"cursor-pointer"}
                    onClick={() => setOpenTandCModal(true)}
                    // onClick={() => navigate(ROUTES.onBoardingTANDC)}
                  >
                    Terms of Use and Conditions
                  </Typography>
                </Typography>{" "}
                &nbsp;
              </div>
            </div>
            <ButtonPair
              loading={isSubmitting}
              disableContinue={!values.acceptedTC}
              onPreviousClick={() => navigate(ROUTES.agentOnboardingStart)}
            />
          </Form>
        )}
      </Formik>
      <TandCModal
        showModal={openTandCModal}
        onClose={() => setOpenTandCModal(false)}
      />
    </div>
  );
};

export default PersonalDetails;
